import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import OnboardBrand from './OnboardBrand';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { useAuth } from '@shared/auth/AuthContext';
import { Skeleton, SectionErrorBoundary, VerifiedBadge } from '@shared/ui';
import { formatMoney } from '@shared/lib/format';
import { useHandleVerification } from '@shared/hooks/useHandleVerification';
import i18n from '@shared/i18n';

// My Analytics — competitive media intelligence for the logged-in brand.
// Three consolidated RPCs (offline / social / presence) feed a single narrative
// page: overview → cross-channel footprint → offline block → social deep-dive.
// See migrations/session_2026_07_18_my_analytics_rpcs.sql.

type Period = 3 | 6 | 12;

const L = () => i18n.language;
const num = (v: unknown): number => { const n = typeof v === 'string' ? parseFloat(v) : (v as number); return Number.isFinite(n) ? n : 0; };

function compact(n: number, mln: string): string {
  const lang = L();
  if (n >= 1e6) return (n / 1e6).toLocaleString(lang, { maximumFractionDigits: 1 }) + ' ' + mln;
  if (n >= 1e4) return Math.round(n / 1e3).toLocaleString(lang) + 'K';
  if (n >= 1e3) return (n / 1e3).toLocaleString(lang, { maximumFractionDigits: 1 }) + 'K';
  return Math.round(n).toLocaleString(lang);
}
const nf = (n: number) => Math.round(n).toLocaleString(L());
const pct = (n: number) => n.toLocaleString(L(), { maximumFractionDigits: 1 }) + '%';

export default function MyAnalytics() {
  const { t } = useTranslation('customer');
  const tr = (k: string, d: string, o?: Record<string, unknown>) => t(k, { defaultValue: d, ...o }) as string;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>(6);
  const [brandId, setBrandId] = useState<string | null>(null); // null = all brands of the advertiser
  const mln = tr('myAnalytics.mln', 'млн');
  const som = tr('myAnalytics.som', 'сом');
  const money = (n: number) => compact(n, mln) + ' ' + som;

  // Brand-level scope (2026-07-25): analytics resolves the caller's brands from their
  // organization_ad_scope, not a single advertiser. get_my_brands() is the switcher
  // roster + the gate — a user with no scoped brand sees the "verify" state.
  const { data: myBrands, isLoading: lp } = useSupabaseQuery<{ brand_id: string; name: string; mentions: number }[]>(
    ['get_my_brands', user?.id],
    async () => {
      if (!user) return { data: [], error: null };
      return supabase.rpc('get_my_brands');
    },
    { enabled: !!user },
  );
  const brands = myBrands ?? [];
  const hasBrands = brands.length > 0;
  // Name shown in the header + used as the "me" label in the SoV race.
  const meName = brandId
    ? brands.find((b) => b.brand_id === brandId)?.name ?? null
    : brands.length === 1 ? brands[0].name : tr('myAnalytics.allBrands', 'Все бренды');
  // In the Share-of-Voice race the "you" row is a competitor comparison, so the
  // multi-brand aggregate reads as "Мой бренд", not the neutral "Все бренды".
  const meLabel = brandId || brands.length === 1 ? meName : tr('myAnalytics.sovMe', 'Мой бренд');

  // Social-only: My Analytics focuses on Instagram. The offline blocks and the
  // cross-channel presence matrix were removed 2026-07-23 per product decision
  // (and those channels themselves were retired 2026-10-27).
  const { data: soc, isLoading: ls, error: socErr } = useSupabaseQuery<any>(
    ['get_my_social_analytics', user?.id, period, brandId],
    async () => hasBrands ? supabase.rpc('get_my_social_analytics', { p_months: period, p_brand: brandId }) : { data: null, error: null },
    { enabled: hasBrands },
  );

  if (lp) return <div style={{ maxWidth: 900 }}><Skeleton height={420} /></div>;

  if (!hasBrands) {
    return (
      <div style={{ maxWidth: 720 }}>
        <h2 className="c-page-title">{tr('myAnalytics.title', 'Моя аналитика')}</h2>
        <div className="c-panel" style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'flex-start' }}>
          <p style={{ margin: 0, color: 'var(--muted)', lineHeight: 1.55 }}>
            {tr('myAnalytics.notLinkedShort', 'Впишите Instagram своего бренда — здесь появятся блогеры, которые о нём пишут, ваша доля голоса и конкуренты.')}
          </p>
        </div>
        {/* The handle field right here — not a button that sends you to another page. */}
        <OnboardBrand />
      </div>
    );
  }

  const S = STYLES;
  return (
    <SectionErrorBoundary name="my-analytics">
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        {/* Header + period */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 className="c-page-title" style={{ margin: 0 }}>
              {tr('myAnalytics.title', 'Моя аналитика')}
              {meName && <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: 15, marginLeft: 8 }}>· {meName}</span>}
            </h2>
            <p style={{ color: 'var(--muted)', fontSize: 13, margin: '4px 0 0' }}>
              {tr('myAnalytics.sub', 'Кто пишет о вашем бренде в Instagram, как вы смотритесь на фоне конкурентов и кого стоит подключить.')}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {([3, 6, 12] as const).map((m) => (
              <button key={m} onClick={() => setPeriod(m)} style={pill(period === m)}>
                {tr('myAnalytics.months', '{{n}} мес', { n: m })}
              </button>
            ))}
          </div>
        </div>

        {/* Brand switcher — an org scoped to multiple brands can drill into one */}
        {brands.length > 1 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 14 }}>
            <button onClick={() => setBrandId(null)} style={pill(brandId === null)}>
              {tr('myAnalytics.allBrands', 'Все бренды')}
            </button>
            {brands.map((b) => (
              <button key={b.brand_id} onClick={() => setBrandId(b.brand_id)}
                style={{ ...pill(brandId === b.brand_id), opacity: num(b.mentions) > 0 ? 1 : 0.5 }}>
                {b.name}{num(b.mentions) > 0 ? ` · ${nf(num(b.mentions))}` : ''}
              </button>
            ))}
          </div>
        )}

        {soc?.has_data ? (
          <>
            {/* Takeaway + next action — reads the data so the user knows what to do */}
            <SocialSummary soc={soc} money={money} mln={mln} tr={tr} onInvite={() => navigate('/customer/campaigns')} />

            {/* KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginTop: 16 }}>
              <StatCard lbl={tr('myAnalytics.s.reach', 'Охват ≈')} big={compact(num(soc.kpis?.reach), mln)} meta={tr('myAnalytics.s.reachMeta', 'просмотров · оценка')} loading={ls} />
              <StatCard lbl={tr('myAnalytics.s.er', 'Медианный ER')} big={soc.kpis?.median_er != null ? pct(num(soc.kpis.median_er)) : '—'} meta={tr('myAnalytics.s.erMeta', 'на пост · резонанс')} loading={ls} />
              <StatCard lbl={tr('myAnalytics.s.mentions', 'Упоминания')} big={nf(num(soc.kpis?.mentions))} meta={tr('myAnalytics.s.mentionsMeta', '{{p}} постов · {{st}} сторис', { p: num(soc.kpis?.posts), st: num(soc.kpis?.stories) })} loading={ls} />
              <StatCard lbl={tr('myAnalytics.s.emv', 'Заработанная стоимость ≈')} big={money(num(soc.kpis?.emv))} meta={tr('myAnalytics.s.emvMeta', 'оценка EMV')} loading={ls} />
            </div>
            <div style={{ ...S.fresh }}>
              {tr('myAnalytics.s.interactions', 'Всего взаимодействий — {{n}} (лайки + комменты, только посты). Фоновый показатель; решения — по охвату, ER и EMV.', { n: nf(num(soc.kpis?.engagement)) })}
            </div>

            {sumPartnership(soc.partnership) > 0 && (
              <Section title={tr('myAnalytics.pe.title', 'Платное или заработанное')} note={tr('myAnalytics.pe.note', 'типы упоминаний')}>
                <PaidEarned p={soc.partnership} tr={tr} />
              </Section>
            )}

            {soc.social_sov?.length > 1 && (
              <Section title={tr('myAnalytics.ssov.title', 'Доля голоса в Instagram')} note={tr('myAnalytics.ssov.note', 'упоминания в категории')}>
                <Race rows={buildSocialRace(soc, meLabel)} fmt={(r) => nf(r.value)} />
              </Section>
            )}

            <Section title={tr('myAnalytics.fmt.title', 'Форматы контента')} note={tr('myAnalytics.fmt.note', 'что и как заходит')}>
              <Formats f={soc.formats} tr={tr} />
            </Section>

            {sumTiers(soc.tiers) > 0 && (
              <Section title={tr('myAnalytics.tiers.title', 'Уровни блогеров')} note={tr('myAnalytics.tiers.note', 'кто вас упоминает')}>
                <Tiers tiers={soc.tiers} tr={tr} />
              </Section>
            )}

            {soc.loyalty && (num(soc.loyalty.exclusive) + num(soc.loyalty.shared)) > 0 && (
              <Section title={tr('myAnalytics.loy.title', 'Лояльность адвокатов')} note={tr('myAnalytics.loy.note', 'эксклюзивность упоминаний')}>
                <Loyalty loy={soc.loyalty} tr={tr} />
              </Section>
            )}

            {soc.top_advocates?.length > 0 && (
              <Section title={tr('myAnalytics.top.title', 'Топ-адвокаты')} note={tr('myAnalytics.top.note', 'по охвату за период')}>
                <TopAdvocates rows={soc.top_advocates} tr={tr} />
              </Section>
            )}

            {soc.whitespace?.length > 0 && (
              <Section title={tr('myAnalytics.ws.title', 'Блогеры, которых стоит подключить')} note={tr('myAnalytics.ws.note', 'пишут о конкурентах, не о вас')}>
                <Whitespace rows={soc.whitespace} tr={tr} onInvite={() => navigate('/customer/campaigns')} />
              </Section>
            )}
          </>
        ) : socErr ? (
          <div className="c-panel" style={{ marginTop: 16, color: 'var(--danger, #e74c3c)' }}>
            {tr('myAnalytics.loadError', 'Не удалось загрузить аналитику — попробуйте обновить страницу.')}
          </div>
        ) : !ls ? (
          // Most freshly verified brands land here: nobody has mentioned them in the window
          // yet. Say why, and give the next useful step instead of a dead end.
          <div className="c-panel" style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ fontWeight: 600 }}>{tr('myAnalytics.noData', 'Пока нет данных по вашему бренду за этот период.')}</div>
            <div style={{ color: 'var(--muted)', lineHeight: 1.55, fontSize: 14 }}>
              {tr('myAnalytics.noDataWhy', 'Здесь появляются посты и сторис блогеров, где упоминается ваш бренд. Мы следим за ними каждый день — как только кто-то упомянет вас, данные появятся сами.')}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {period < 12 && (
                <button onClick={() => setPeriod(12)} style={pill(false)}>{tr('myAnalytics.noDataWiden', 'Показать за 12 месяцев')}</button>
              )}
              <button onClick={() => navigate('/customer/market/social')} style={pill(false)}>{tr('myAnalytics.noDataBrowse', 'Кто рекламирует вашу категорию →')}</button>
              <button onClick={() => navigate('/customer/campaigns')} style={pill(false)}>{tr('myAnalytics.noDataCampaign', 'Разместить задание для блогеров →')}</button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 16 }}><Skeleton height={320} /></div>
        )}

        {/* Add or drop followed brands — lives here, next to their analytics. */}
        <details style={{ marginTop: 20 }}>
          <summary style={{ cursor: 'pointer', color: 'var(--accent-text)', fontSize: 14, minHeight: 44, display: 'flex', alignItems: 'center' }}>
            {tr('myAnalytics.manageBrands', 'Управлять брендами')}
          </summary>
          <OnboardBrand />
        </details>
      </div>
    </SectionErrorBoundary>
  );
}

/* ───────────────────────── helpers ───────────────────────── */

function buildSocialRace(soc: any, meName: string | null): RaceRow[] {
  return (soc.social_sov ?? []).map((r: any) => ({ name: r.is_me ? (meName ?? r.name) : r.name, value: num(r.mentions), cost: 0, is_me: !!r.is_me }));
}

const sumPartnership = (p: any) => p ? Object.values(p).reduce((s: number, v: any) => s + num(v), 0) : 0;
const sumTiers = (t: any) => t ? Object.values(t).reduce((s: number, v: any) => s + num(v), 0) : 0;

type RaceRow = { name: string; value: number; cost: number; is_me: boolean };

/* ───────────────────────── shared UI ───────────────────────── */

function pill(active: boolean): React.CSSProperties {
  return {
    fontSize: 12, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
    border: active ? '1px solid transparent' : '1px solid var(--border-strong)',
    background: active ? 'rgba(74,158,255,0.14)' : 'transparent',
    color: active ? '#bcd9ff' : 'var(--muted)',
  };
}

function Section({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: 26 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>{title}</h3>
        {note && <span style={{ fontSize: 12, color: 'var(--dim)', marginLeft: 'auto' }}>{note}</span>}
      </div>
      <div className="c-panel">{children}</div>
    </section>
  );
}

function StatCard({ lbl, big, meta, loading }: { lbl: string; big: string; meta?: string; loading?: boolean }) {
  return (
    <div style={{ background: 'var(--bg-elev)', border: '1px solid var(--border)', borderRadius: 12, padding: '15px 16px' }}>
      <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{lbl}</div>
      <div style={{ fontSize: 26, fontWeight: 600, marginTop: 7, letterSpacing: -0.5, fontVariantNumeric: 'tabular-nums' }}>{loading ? '…' : big}</div>
      {meta && <div style={{ fontSize: 11.5, color: 'var(--dim)', marginTop: 5 }}>{meta}</div>}
    </div>
  );
}

function Race({ rows, fmt, tail }: { rows: RaceRow[]; fmt: (r: RaceRow) => string; tail?: string }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '128px 1fr 132px', alignItems: 'center', gap: 14 }}>
          <div style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: r.is_me ? '#bcd9ff' : 'var(--text)', fontWeight: r.is_me ? 600 : 400 }}>
            <span style={{ color: 'var(--dim)', marginRight: 8 }}>{i + 1}</span>{r.name}
          </div>
          <div style={{ height: 23, background: 'var(--bg-soft)', borderRadius: 7, overflow: 'hidden' }}>
            <div style={{ width: `${(r.value / max) * 100}%`, height: '100%', borderRadius: 7, background: r.is_me ? 'var(--accent)' : '#33465f' }} />
          </div>
          <div style={{ textAlign: 'right', fontSize: 12.5, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>
            <b style={{ color: r.is_me ? '#bcd9ff' : 'var(--text)', fontWeight: 600 }}>{fmt(r).split(' · ')[0]}</b>{fmt(r).includes(' · ') ? ' · ' + fmt(r).split(' · ')[1] : ''}
          </div>
        </div>
      ))}
      {tail && <div style={{ fontSize: 11.5, color: 'var(--dim)', marginTop: 6, paddingLeft: 2 }}>{tail}</div>}
    </div>
  );
}

// Reads the social data and tells the user the takeaway + the next action.
function SocialSummary({ soc, money, mln, tr, onInvite }: { soc: any; money: (n: number) => string; mln: string; tr: any; onInvite: () => void }) {
  const k = soc.kpis ?? {};
  const creators = num(k.creators), reach = num(k.reach), emv = num(k.emv);
  const ws = soc.whitespace?.length ?? 0;
  const shared = num(soc.loyalty?.shared);
  return (
    <div style={{ marginTop: 16, borderRadius: 12, padding: '16px 18px', background: 'rgba(74,158,255,0.08)', border: '1px solid rgba(74,158,255,0.22)' }}>
      <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--text)' }}>
        {tr('myAnalytics.summary.line', 'О вашем бренде пишут {{c}} блогеров · охват ≈ {{r}} · оценка EMV {{e}}.', { c: nf(creators), r: compact(reach, mln), e: money(emv) })}
      </div>
      {ws > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>{tr('myAnalytics.summary.ws', '{{n}} блогеров пишут о конкурентах, но не о вас — их стоит подключить.', { n: ws })}</span>
          <button onClick={onInvite} style={{ fontSize: 13, fontWeight: 600, color: '#fff', background: 'var(--primary)', border: 'none', padding: '8px 15px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
            {tr('myAnalytics.summary.cta', 'Разместить задание')}
          </button>
        </div>
      ) : shared > 0 ? (
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 10 }}>{tr('myAnalytics.summary.shared', '{{n}} блогеров пишут и о вас, и о конкурентах — стоит закрепить эксклюзивом.', { n: shared })}</div>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 10 }}>{tr('myAnalytics.summary.good', 'Работайте с топ-адвокатами ниже, чтобы усилить присутствие.')}</div>
      )}
    </div>
  );
}

function PaidEarned({ p, tr }: { p: any; tr: any }) {
  const organic = num(p.organic), paid = num(p.paid) + num(p.ambassador), give = num(p.giveaway) + num(p.other);
  const total = organic + paid + give || 1;
  const seg = (v: number, c: string, label?: string) => v > 0 ? <span style={{ width: `${(v / total) * 100}%`, background: c, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: '#0d1117' }}>{label}</span> : null;
  const oPct = Math.round((organic / total) * 100);
  return (
    <>
      <div style={{ display: 'flex', height: 26, borderRadius: 7, overflow: 'hidden', marginBottom: 12 }}>
        {seg(organic, '#2ecc71', `${oPct}% ${tr('myAnalytics.pe.organic', 'органика')}`)}
        {seg(paid, '#4a9eff')}
        {seg(give, '#9B5DE5')}
      </div>
      <div style={{ display: 'flex', gap: 16, fontSize: 11.5, color: 'var(--muted)', flexWrap: 'wrap' }}>
        <Key c="#2ecc71" label={`${tr('myAnalytics.pe.organic', 'органика')} ${nf(organic)}`} />
        <Key c="#4a9eff" label={`${tr('myAnalytics.pe.paid', 'платно')} ${nf(paid)}`} />
        <Key c="#9B5DE5" label={`${tr('myAnalytics.pe.give', 'giveaway')} ${nf(give)}`} />
      </div>
      {organic / total >= 0.5 && (
        <Insight kind="ok">{tr('myAnalytics.pe.insight', '{{p}}% упоминаний — органические: люди отмечают вас без оплаты.', { p: oPct })}</Insight>
      )}
    </>
  );
}

function Formats({ f, tr }: { f: any; tr: any }) {
  const cards = [
    { k: 'story', label: tr('myAnalytics.fmt.story', 'Сторис'), count: num(f?.story?.count), meta: '' },
    { k: 'reel', label: tr('myAnalytics.fmt.reel', 'Рилс'), count: num(f?.reel?.count), meta: f?.reel?.er != null ? `${tr('myAnalytics.fmt.erAuthors', 'ER авторов')} ${pct(num(f.reel.er))}` : '', badge: tr('myAnalytics.fmt.best', 'выше ER у авторов') },
    { k: 'post', label: tr('myAnalytics.fmt.post', 'Посты'), count: num(f?.post?.count), meta: f?.post?.er != null ? `${tr('myAnalytics.fmt.erAuthors', 'ER авторов')} ${pct(num(f.post.er))}` : '' },
  ];
  const reelBetter = num(f?.reel?.er) > num(f?.post?.er) && num(f?.reel?.count) > 0;
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
        {cards.map((c) => (
          <div key={c.k} style={{ background: 'var(--bg-soft)', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 12.5, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
              {c.label}{c.badge && reelBetter && c.k === 'reel' && <span style={{ fontSize: 10, fontWeight: 600, color: '#bcd9ff', background: 'rgba(74,158,255,0.14)', padding: '1px 7px', borderRadius: 20, marginLeft: 'auto' }}>{c.badge}</span>}
            </div>
            <div style={{ fontSize: 20, fontWeight: 600, marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>{nf(c.count)}</div>
            {c.meta && <div style={{ fontSize: 11, color: 'var(--dim)', marginTop: 4 }}>{c.meta}</div>}
          </div>
        ))}
      </div>
      {reelBetter && <Insight kind="acc">{tr('myAnalytics.fmt.insight', 'Рилс чаще снимают блогеры с более высоким ER (это корреляция, а не эффект самого формата).')}</Insight>}
    </>
  );
}

function Tiers({ tiers, tr }: { tiers: any; tr: any }) {
  const order = [
    { k: 'nano', label: tr('myAnalytics.tiers.nano', 'Нано · <10K') },
    { k: 'micro', label: tr('myAnalytics.tiers.micro', 'Микро · 10–100K') },
    { k: 'macro', label: tr('myAnalytics.tiers.macro', 'Макро · 100K–1M') },
    { k: 'mega', label: tr('myAnalytics.tiers.mega', 'Мега · >1M') },
  ];
  const max = Math.max(1, ...order.map((o) => num(tiers?.[o.k])));
  const top = order.reduce((a, b) => (num(tiers?.[b.k]) > num(tiers?.[a.k]) ? b : a), order[0]);
  return (
    <>
      {order.map((o) => (
        <div key={o.k} style={{ display: 'grid', gridTemplateColumns: '130px 1fr 40px', alignItems: 'center', gap: 12, marginBottom: 9, fontSize: 12.5 }}>
          <span style={{ color: 'var(--muted)' }}>{o.label}</span>
          <div style={{ height: 16, background: 'var(--bg-soft)', borderRadius: 5, overflow: 'hidden' }}>
            <div style={{ width: `${(num(tiers?.[o.k]) / max) * 100}%`, height: '100%', background: 'var(--accent)', borderRadius: 5 }} />
          </div>
          <span style={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{nf(num(tiers?.[o.k]))}</span>
        </div>
      ))}
      {(top.k === 'micro' || top.k === 'nano') && <Insight kind="acc">{tr('myAnalytics.tiers.insight', 'Ваша сила — небольшие блогеры: широкая органическая база, а не пара крупных.')}</Insight>}
    </>
  );
}

function Loyalty({ loy, tr }: { loy: any; tr: any }) {
  const cards = [
    { v: num(loy.exclusive), label: tr('myAnalytics.loy.exclusive', 'Пишут только о вас'), tone: 'good' },
    { v: num(loy.shared), label: tr('myAnalytics.loy.shared', 'И о вас, и о конкурентах'), tone: 'risk' },
    { v: num(loy.ambassadors), label: tr('myAnalytics.loy.ambassadors', 'Ваши амбассадоры'), tone: '' },
  ];
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, textAlign: 'center' }}>
        {cards.map((c, i) => (
          <div key={i} style={{ background: 'var(--bg-soft)', border: `1px solid ${c.tone === 'risk' ? 'rgba(243,156,18,0.3)' : 'var(--border)'}`, borderRadius: 10, padding: '15px 10px' }}>
            <div style={{ fontSize: 24, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: c.tone === 'risk' ? 'var(--warn)' : c.tone === 'good' ? 'var(--success)' : 'var(--text)' }}>{nf(c.v)}</div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4 }}>{c.label}</div>
          </div>
        ))}
      </div>
      {num(loy.shared) > 0 && <Insight kind="warn">{tr('myAnalytics.loy.insight', '{{n}} блогеров отмечают и вас, и конкурентов — зона риска. Стоит закрепить эксклюзивом.', { n: num(loy.shared) })}</Insight>}
    </>
  );
}

function TopAdvocates({ rows, tr }: { rows: any[]; tr: any }) {
  const verif = useHandleVerification(rows.map((r) => r.handle));
  const tierL: Record<string, string> = { nano: tr('myAnalytics.tiers.nanoS', 'Нано'), micro: tr('myAnalytics.tiers.microS', 'Микро'), macro: tr('myAnalytics.tiers.macroS', 'Макро'), mega: tr('myAnalytics.tiers.megaS', 'Мега') };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 520 }}>
        <thead><tr style={{ color: 'var(--dim)', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.3 }}>
          <th style={{ textAlign: 'left', padding: '4px 8px 9px' }}>{tr('myAnalytics.top.creator', 'Блогер')}</th>
          <th style={{ textAlign: 'right', padding: '4px 8px 9px' }}>{tr('myAnalytics.top.tier', 'Уровень')}</th>
          <th style={{ textAlign: 'right', padding: '4px 8px 9px' }}>{tr('myAnalytics.top.followers', 'Подписчики')}</th>
          <th style={{ textAlign: 'right', padding: '4px 8px 9px' }}>ER</th>
          <th style={{ textAlign: 'right', padding: '4px 8px 9px' }}>{tr('myAnalytics.top.reach', 'Охват ≈')}</th>
          <th style={{ textAlign: 'right', padding: '4px 8px 9px' }}>{tr('myAnalytics.top.mentions', 'Упом.')}</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ textAlign: 'left', padding: '9px 8px', fontWeight: 600 }}>@{r.handle} <VerifiedBadge {...verif(r.handle)} size={14} /></td>
              <td style={{ textAlign: 'right', padding: '9px 8px', color: 'var(--dim)', fontSize: 10.5 }}>{tierL[r.tier] ?? r.tier}</td>
              <td style={{ textAlign: 'right', padding: '9px 8px', fontVariantNumeric: 'tabular-nums' }}>{nf(num(r.followers))}</td>
              <td style={{ textAlign: 'right', padding: '9px 8px', fontVariantNumeric: 'tabular-nums' }}>{r.er != null ? pct(num(r.er)) : '—'}</td>
              <td style={{ textAlign: 'right', padding: '9px 8px', fontVariantNumeric: 'tabular-nums' }}>{nf(num(r.reach))}</td>
              <td style={{ textAlign: 'right', padding: '9px 8px', fontVariantNumeric: 'tabular-nums' }}>{nf(num(r.mentions))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Whitespace({ rows, tr, onInvite }: { rows: any[]; tr: any; onInvite: () => void }) {
  const verif = useHandleVerification(rows.map((r) => r.handle));
  return (
    <>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'center', gap: 16, padding: '12px 2px', borderTop: i === 0 ? 'none' : '1px solid var(--border)' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>@{r.handle} <VerifiedBadge {...verif(r.handle)} size={14} />{r.country && <span style={{ marginLeft: 6, color: 'var(--dim)', fontWeight: 400, fontSize: 11 }}>{r.country}</span>}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
              <Tag>{tr('myAnalytics.ws.followers', '{{n}} подписчиков', { n: nf(num(r.followers)) })}</Tag>
              <Tag dim={r.er == null}>ER {r.er != null ? pct(num(r.er)) : '—'}</Tag>
              {r.niche && <Tag dim>{r.niche}</Tag>}
            </div>
          </div>
          <div style={{ textAlign: 'right', minWidth: 72, fontVariantNumeric: 'tabular-nums' }}>
            <b style={{ fontSize: 14, fontWeight: 600 }}>{nf(num(r.competitor_mentions))}</b>
            <span style={{ display: 'block', fontSize: 10, color: 'var(--dim)' }}>{tr('myAnalytics.ws.atComp', 'у конкурентов')}</span>
          </div>
          <button onClick={onInvite} style={{ fontSize: 12, fontWeight: 600, color: '#bcd9ff', background: 'rgba(74,158,255,0.12)', border: '1px solid rgba(74,158,255,0.3)', padding: '7px 13px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
            {tr('myAnalytics.ws.invite', 'Пригласить')}
          </button>
        </div>
      ))}
    </>
  );
}

function Insight({ kind, children }: { kind: 'acc' | 'warn' | 'ok'; children: React.ReactNode }) {
  const c = kind === 'warn'
    ? { bg: 'rgba(243,156,18,0.07)', bd: 'rgba(243,156,18,0.26)', fg: '#e6d3b0' }
    : kind === 'ok'
      ? { bg: 'rgba(46,204,113,0.07)', bd: 'rgba(46,204,113,0.24)', fg: '#c2e6cf' }
      : { bg: 'rgba(74,158,255,0.07)', bd: 'rgba(74,158,255,0.22)', fg: '#d4dbe6' };
  return <div style={{ fontSize: 12.5, lineHeight: 1.45, borderRadius: 10, padding: '11px 13px', marginTop: 14, background: c.bg, border: `1px solid ${c.bd}`, color: c.fg }}>{children}</div>;
}

function Tag({ children, dim }: { children: React.ReactNode; dim?: boolean }) {
  return <span style={{ fontSize: 11, color: dim ? 'var(--dim)' : 'var(--muted)', background: 'var(--bg-soft)', border: '1px solid var(--border)', padding: '1px 8px', borderRadius: 20, fontVariantNumeric: 'tabular-nums' }}>{children}</span>;
}
function Key({ c, label }: { c: string; label: string }) {
  return <span><i style={{ width: 9, height: 9, borderRadius: 2, display: 'inline-block', marginRight: 5, background: c }} />{label}</span>;
}

const STYLES = {
  fresh: { fontSize: 11.5, color: 'var(--dim)', marginTop: 12 } as React.CSSProperties,
  gap: { color: '#ef8b80', fontWeight: 600, fontSize: 11 } as React.CSSProperties,
};
const S = STYLES;
